import React, { useState, useEffect } from 'react';
import { scrollToSection } from '../utils/smoothScroll';

/**
 * NavigationHeader Component
 * Sticky header with navigation tabs for smooth scrolling to sections
 * Features:
 * - Sticky positioning at top of viewport
 * - Navigation tabs: Home, About Us, Collections, Gallery, Contact
 * - Smooth scroll on tab click
 * - Dark theme with gold accents
 * - Responsive design (mobile hamburger menu / desktop horizontal nav)
 * - Active section tracking
 * - Mobile: Full-screen overlay menu with hamburger icon
 */

interface NavigationTab {
  id: string;
  label: string;
  sectionId: string;
  icon: string;
}

const navigationTabs: NavigationTab[] = [
  { id: 'home', label: 'Home', sectionId: 'hero', icon: '🏠' },
  { id: 'about', label: 'About Us', sectionId: 'about', icon: 'ℹ️' },
  { id: 'collections', label: 'Collections', sectionId: 'collections', icon: '💎' },
  { id: 'gallery', label: 'Gallery', sectionId: 'gallery', icon: '🖼️' },
  { id: 'rates', label: 'Rates', sectionId: 'rates', icon: '📊' },
  { id: 'why-us', label: 'Why Us', sectionId: 'why-us', icon: '⭐' },
  { id: 'contact', label: 'Contact', sectionId: 'contact', icon: '📞' },
];

export const NavigationHeader: React.FC<{ onMenuStateChange?: (isOpen: boolean) => void }> = ({ onMenuStateChange }) => {
  const [activeSection, setActiveSection] = useState<string>('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  useEffect(() => {
    // Notify parent component when menu state changes
    if (onMenuStateChange) {
      onMenuStateChange(isMobileMenuOpen);
    }
  }, [isMobileMenuOpen, onMenuStateChange]);

  useEffect(() => {
    // Track active section based on scroll position
    const handleScroll = () => {
      const sections = navigationTabs.map(tab => tab.sectionId);
      
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Check if section is in viewport (with offset for header)
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  const handleNavClick = (sectionId: string) => {
    scrollToSection(sectionId);
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const handleEnquireClick = () => {
    scrollToSection('contact');
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="sticky top-0 z-50 bg-black shadow-lg">
      <nav className="mx-auto px-6 py-4" style={{ maxWidth: '1420px' }}>
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="/Logo.png" 
              alt="Siyaram Jewellers" 
              className="h-11"
              style={{ height: '45px', width: 'auto' }}
            />
          </div>

          {/* Center Navigation */}
          <ul className="flex space-x-8">
            {navigationTabs.map((tab) => (
              <li key={tab.id}>
                <button
                  onClick={() => handleNavClick(tab.sectionId)}
                  className={`
                    px-2 py-2 text-sm font-medium transition-colors duration-200
                    hover:text-yellow-500
                    ${
                      activeSection === tab.sectionId
                        ? 'text-yellow-500'
                        : 'text-gray-100'
                    }
                  `}
                  aria-label={`Navigate to ${tab.label}`}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Enquire Now Button */}
          <button
            onClick={handleEnquireClick}
            className="bg-yellow-500 text-black px-6 py-2 rounded-full font-semibold hover:bg-yellow-400 transition-colors duration-200"
            aria-label="Enquire Now"
          >
            Enquire Now
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/Logo.png" 
                alt="Siyaram Jewellers" 
                className="h-10"
                style={{ height: '40px', width: 'auto' }}
              />
            </div>

            {/* Hamburger Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="p-3 text-yellow-500 hover:text-yellow-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 rounded-lg"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                // Close Icon (X)
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                // Hamburger Icon
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black bg-opacity-95 z-50 animate-fadeIn"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-hidden="true"
              />

              {/* Menu Panel */}
              <div className="fixed inset-y-0 right-0 w-full bg-black z-50 overflow-y-auto animate-slideInRight">
                <div className="flex flex-col h-full">
                  {/* Logo at Top */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                    <div className="flex items-center justify-center flex-1">
                      <img 
                        src="/Logo.png" 
                        alt="Siyaram Jewellers" 
                        className="h-12"
                        style={{ height: '50px', width: 'auto' }}
                      />
                    </div>
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2 text-yellow-500 hover:text-yellow-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 rounded-lg absolute right-6"
                      aria-label="Close menu"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Menu Items */}
                  <nav className="flex-1 px-6 py-8 space-y-2">
                    {navigationTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => handleNavClick(tab.sectionId)}
                        className={`
                          w-full flex items-center space-x-3 px-6 py-5 rounded-lg text-left transition-all duration-200
                          border-b border-gray-800
                          ${
                            activeSection === tab.sectionId
                              ? 'bg-yellow-500 bg-opacity-10 text-yellow-500'
                              : 'text-white hover:bg-gray-900'
                          }
                        `}
                        aria-label={`Navigate to ${tab.label}`}
                      >
                        <span className="text-2xl">{tab.icon}</span>
                        <span className="text-lg font-medium">{tab.label}</span>
                      </button>
                    ))}
                  </nav>

                  {/* Enquire Now Button at Bottom */}
                  <div className="px-6 py-6 border-t border-gray-800">
                    <button
                      onClick={handleEnquireClick}
                      className="w-full bg-yellow-500 text-black px-6 py-4 rounded-full text-base font-bold hover:bg-yellow-400 transition-colors duration-200 flex items-center justify-center space-x-2"
                      aria-label="Enquire Now"
                    >
                      <span>📧</span>
                      <span>Enquire Now</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </nav>
    </header>
  );
};
